import Vue from 'vue'
import Vuex from 'vuex'
const version = require('../../version.json')
const demo = require('../../demo.json')

Vue.use(Vuex)

const colors = {
  primary: '#fdc400',
  info: '#0070e0',
  warning: '#ffff66',
  lightdanger: '#db3645',
  danger: '#a1210a',
  lightsuccess: '#8ffbaf',
  success: '#009303',
  light: '#f1f1f1',
  dark: '#605e5b',
  opacsuccess: 'rgba(143, 251, 175, 0.1)',
  opacwarning: 'rgba(255, 255, 102, 0.1)',
  opacdanger: 'rgba(219, 54, 69, 0.1)'
}

const state = {
  nextAlertId: 0,
  alerts: [],
  demo: demo,
  demoSettings: {
    enabled: false,
    dt: 0.5,
    duration: 700,
    maxDuration: 700,
    minDuration: 10,
    nextIndex: 0,
    indexInc: 1,
    maxIndex: 700 / 0.5 - 1
  },
  demoTimer: null,
  device: null,
  showHistory: null,
  nmaxHistory: 50,
  version: version.tag,
  connected: null,
  pageTitle: 'pageTitle',
  pageIcon: null,
  specs: {
    serialNumber: '2017160489',
    gearboxType: 'LPI 700',
    hoursOfOperation: 1450,
    appVersion: version.tag
  },
  colors: colors,
  healthStatus: null,
  operatingStatus: null,
  sensors: {
    acx: {
      name: 'accX',
      label: 'X Acceleration',
      unit: 'g',
      icon: 'heart-rate',
      min: 0,
      max: 8,
      value: null,
      timestamp: null,
      status: null,
      history: [],
      valueFormatter: (value) => {
        const precision = 2

        try {
          return value.toFixed(precision)
        } catch (err) {
          return value
        }
      }
    },
    acy: {
      name: 'accY',
      label: 'Y Acceleration',
      unit: 'g',
      icon: 'heart-rate',
      min: 0,
      max: 8,
      value: null,
      timestamp: null,
      status: null,
      history: [],
      valueFormatter: (value) => {
        const precision = 2

        try {
          return value.toFixed(precision)
        } catch (err) {
          return value
        }
      }
    },
    acz: {
      name: 'accZ',
      label: 'Z Acceleration',
      unit: 'g',
      icon: 'heart-rate',
      min: 0,
      max: 8,
      value: null,
      timestamp: null,
      status: null,
      history: [],
      valueFormatter: (value) => {
        const precision = 2

        try {
          return value.toFixed(precision)
        } catch (err) {
          return value
        }
      }
    },
    tge: {
      name: 'temp1',
      label: 'Gear Temperature',
      unit: '°C',
      icon: 'thermometer-half',
      min: 20,
      max: 110,
      valueRanges: [
        { begin: 20, end: 70, backgroundColor: colors.opacsuccess },
        { begin: 70, end: 90, backgroundColor: colors.opacwarning },
        { begin: 90, end: 110, backgroundColor: colors.opacdanger }
      ],
      marker: false,
      value: null,
      timestamp: null,
      status: null,
      history: [],
      checks: [
        { check: (val) => { return val > 70 && val <= 90 }, color: colors.warning, status: 'replace' },
        { check: (val) => { return val > 90 }, color: colors.lightdanger, status: 'failure' }
      ],
      valueFormatter: (value) => {
        const precision = 1

        try {
          return value.toFixed(precision)
        } catch (err) {
          return value
        }
      }
    },
    toi: {
      name: 'temp2',
      label: 'Oil Temperature',
      unit: '°C',
      icon: 'oil-temp',
      min: 20,
      max: 110,
      valueRanges: [
        { begin: 20, end: 70, backgroundColor: colors.opacsuccess },
        { begin: 70, end: 90, backgroundColor: colors.opacwarning },
        { begin: 90, end: 110, backgroundColor: colors.opacdanger }
      ],
      marker: false,
      value: null,
      timestamp: null,
      status: null,
      history: [],
      checks: [
        { check: (val) => { return val > 70 && val <= 90 }, color: colors.warning, status: 'warning' },
        { check: (val) => { return val > 90 }, color: colors.lightdanger, status: 'danger' }
      ],
      valueFormatter: (value) => {
        const precision = 1

        try {
          return value.toFixed(precision)
        } catch (err) {
          return value
        }
      }
    },
    spd: {
      name: 'speed',
      label: 'Speed',
      unit: 'rpm',
      icon: 'tachometer',
      min: -4500,
      max: 4500,
      marker: true,
      value: null,
      timestamp: null,
      status: null,
      history: []
    },
    trq: {
      name: 'torque',
      label: 'Torque',
      unit: 'Nm',
      icon: 'redo',
      min: -500,
      max: 500,
      marker: true,
      value: null,
      timestamp: null,
      status: null,
      history: []
    },
    loi: {
      name: 'oilLevel',
      label: 'Oil Level',
      unit: '%',
      icon: 'oil-can',
      value: null,
      valueRanges: [
        { begin: 0, end: 50, backgroundColor: colors.opacdanger },
        { begin: 50, end: 80, backgroundColor: colors.opacwarning },
        { begin: 80, end: 100, backgroundColor: colors.opacsuccess }
      ],
      timestamp: null,
      status: null,
      history: [],
      checks: [
        { check: (val) => { return val < 80 && val >= 50 }, color: colors.warning, status: 'replace' },
        { check: (val) => { return val < 50 }, color: colors.lightdanger, status: 'failure' }
      ]
    },
    woi: {
      name: 'oilWater',
      label: 'Oil Water',
      unit: '%',
      icon: 'tint',
      value: null,
      timestamp: null,
      status: null,
      history: []
    },
    coi: {
      name: 'oilConduct',
      label: 'Oil Conductivity',
      unit: 'pS',
      icon: 'bolt',
      value: null,
      timestamp: null,
      status: null,
      history: []
    },
    spo: {
      name: 'outputSpeed',
      label: 'Output Speed',
      unit: 'rpm',
      icon: 'tachometer',
      min: -72,
      max: 72,
      valueRanges: [
        { begin: -72, end: -65, backgroundColor: colors.opacdanger },
        { begin: -65, end: -60, backgroundColor: colors.opacwarning },
        { begin: -60, end: 60, backgroundColor: colors.opacsuccess },
        { begin: 60, end: 65, backgroundColor: colors.opacwarning },
        { begin: 65, end: 72, backgroundColor: colors.opacdanger }
      ],
      marker: true,
      value: null,
      timestamp: null,
      status: null,
      history: [],
      checks: [
        { check: (val) => { return Math.abs(val) > 60 && Math.abs(val) <= 65 }, color: colors.warning, status: 'replace' },
        { check: (val) => { return Math.abs(val) > 65 }, color: colors.lightdanger, status: 'failure' }
      ],
      valueFormatter: (value) => {
        const precision = 1

        try {
          return value.toFixed(precision)
        } catch (err) {
          return value
        }
      }
    },
    tro: {
      name: 'outputTorque',
      label: 'Output Torque',
      unit: 'kNm',
      icon: 'redo',
      min: -40,
      max: 40,
      valueRanges: [
        { begin: -40, end: -37, backgroundColor: colors.opacdanger },
        { begin: -37, end: -30, backgroundColor: colors.opacwarning },
        { begin: -30, end: 30, backgroundColor: colors.opacsuccess },
        { begin: 30, end: 37, backgroundColor: colors.opacwarning },
        { begin: 37, end: 40, backgroundColor: colors.opacdanger }
      ],
      marker: true,
      value: null,
      timestamp: null,
      status: null,
      history: [],
      checks: [
        { check: (val) => { return Math.abs(val) > 30 && Math.abs(val) <= 37 }, color: colors.warning, status: 'replace' },
        { check: (val) => { return Math.abs(val) > 37 }, color: colors.lightdanger, status: 'failure' }
      ],
      valueFormatter: (value) => {
        const precision = 1

        try {
          return value.toFixed(precision)
        } catch (err) {
          return value
        }
      }
    },
    qoi: {
      name: 'oilQuality',
      label: 'Oil Quality',
      unit: '%',
      icon: 'clipboard-check',
      value: null,
      valueFormatter: (value, status, checkStatus) => {
        return checkStatus || 'good'
      },
      checks: [
        { check: (val) => { return val < 40 && val >= 20 }, color: colors.warning, status: 'replace' },
        { check: (val) => { return val < 20 }, color: colors.lightdanger, status: 'failure' }
      ],
      timestamp: null,
      status: null,
      history: [],
      labels: {
        min: false,
        max: false,
        unit: false
      },
      css: {
        fontSize: '2rem',
        bottom: '10%'
      }
    },
    hge: {
      name: 'gearboxHealth',
      label: 'Gearbox Health',
      unit: '%',
      icon: 'cogs',
      value: null,
      valueRanges: [
        { begin: 0, end: 20, backgroundColor: colors.opacdanger },
        { begin: 20, end: 40, backgroundColor: colors.opacwarning },
        { begin: 40, end: 100, backgroundColor: colors.opacsuccess }
      ],
      valueFormatter: (value, status, checkStatus) => {
        return checkStatus || 'good'
      },
      checks: [
        { check: (val) => { return val < 40 && val >= 20 }, color: colors.warning, status: 'replace' },
        { check: (val) => { return val < 20 }, color: colors.lightdanger, status: 'failure' }
      ],
      timestamp: null,
      status: null,
      history: [],
      labels: {
        min: false,
        max: false,
        unit: false
      },
      css: {
        fontSize: '2rem',
        bottom: '10%'
      }
    },
    lft: {
      name: 'lifetime',
      label: 'Remaining Lifetime',
      unit: '%',
      icon: 'heartbeat',
      value: null,
      valueRanges: [
        { begin: 0, end: 20, backgroundColor: colors.opacdanger },
        { begin: 20, end: 40, backgroundColor: colors.opacwarning },
        { begin: 40, end: 100, backgroundColor: colors.opacsuccess }
      ],
      timestamp: null,
      status: null,
      history: [],
      checks: [
        { check: (val) => { return val < 40 && val >= 20 }, color: colors.warning, status: 'warning' },
        { check: (val) => { return val < 20 }, color: colors.lightdanger, status: 'danger' }
      ]
    }
  }
}

const getters = {
  alerts (state) {
    return state.alerts
  },
  device (state) {
    return state.device
  },
  demo (state) {
    return state.demo
  },
  demoSettings (state) {
    return state.demoSettings
  },
  demoTimer (state) {
    return state.demoTimer
  },
  dummy (state) {
    return state.dummy
  },
  connected (state) {
    return state.connected
  },
  colors (state) {
    return state.colors
  },
  sensors (state) {
    return state.sensors
  },
  version (state) {
    return state.version
  },
  specs (state) {
    return state.specs
  },
  pageTitle (state) {
    return state.pageTitle
  },
  pageIcon (state) {
    return state.pageIcon
  }
}

const mutations = {
  pushAlert (state, payload) {
    payload.id = state.nextAlertId
    payload.timestamp = Date.now()
    state.alerts.unshift(payload)
    Vue.set(state, 'nextAlertId', state.nextAlertId + 1)
  },
  dismissAlertById (state, payload) {
    const idx = state.alerts.findIndex((alert) => {
      return alert.id === payload
    })

    if (idx !== -1) {
      state.alerts.splice(idx, 1)
    }
  },
  setDevice (state, payload) {
    Vue.set(state, 'device', payload)
  },
  setDemoTimer (state, payload) {
    Vue.set(state, 'demoTimer', payload)
  },
  toggleDemo (state) {
    Vue.set(state.demoSettings, 'enabled', !state.demoSettings.enabled)
  },
  setDemoDuration (state, payload) {
    Vue.set(state.demoSettings, 'duration', payload)
  },
  setDemoIndexInc (state, payload) {
    Vue.set(state.demoSettings, 'indexInc', payload)
  },
  setConnected (state, payload) {
    state.connected = payload
  },
  resetDemoNextIndex (state) {
    Vue.set(state.demoSettings, 'nextIndex', 0)
  },
  pushDemoData (state) {
    console.log('pushDemoData')
    const idx = state.demoSettings.nextIndex
    const inc = state.demoSettings.indexInc
    const maxIdx = state.demoSettings.maxIndex
    const nextIdx = (idx + inc) % maxIdx
    Vue.set(state.demoSettings, 'nextIndex', nextIdx)
    const dataIdx = Math.round(idx)

    _pushDemoData(state, dataIdx)

    function _pushDemoData (state, idx) {
      const timestamp = Date.now()

      Object.keys(state.demo.indexOf).forEach((key) => {
        const ch = state.demo.indexOf[key]
        const scale = state.demo.scale[key] || 1
        const value = state.demo.dataset[idx][ch] * scale

        Vue.set(state.sensors[key], 'value', value)
        Vue.set(state.sensors[key], 'timestamp', timestamp)

        state.sensors[key].history.push({
          x: timestamp,
          y: value
        })

        Vue.set(state.sensors[key], 'history', state.sensors[key].history.slice(-state.nmaxHistory))
      })
    }
  },
  pushSensorData (state, payload) {
    _pushSensorData(state, payload)

    if (payload.id === 'trq') {
      const trqValue = payload.value
      const spdValue = state.sensors.spd.value

      let troValue = NaN
      if (Math.abs(trqValue) < 40) {
        troValue = 0
      } else if (Math.abs(spdValue) >= 0.1) {
        troValue = trqValue * 64 * Math.pow(0.98, Math.sign(trqValue * spdValue)) / 1000
      } else if (Math.abs(spdValue) < 0.1) {
        troValue = trqValue * 64 * Math.pow(0.98, -1) / 1000
      }

      const _payload = {
        id: 'tro',
        timestamp: payload.timestamp,
        value: troValue
      }

      _pushSensorData(state, _payload)
    }

    if (payload.id === 'spd') {
      const _payload = {
        id: 'spo',
        timestamp: payload.timestamp,
        value: payload.value / 64
      }

      _pushSensorData(state, _payload)
    }

    if (payload.id === 'toi') {
      const lftPayload = {
        id: 'lft',
        timestamp: payload.timestamp,
        value: 99
      }

      const hgePayload = {
        id: 'hge',
        timestamp: payload.timestamp,
        value: 99
      }

      const qoiPayload = {
        id: 'qoi',
        timestamp: payload.timestamp,
        value: 99
      }

      _pushSensorData(state, lftPayload)
      _pushSensorData(state, hgePayload)
      _pushSensorData(state, qoiPayload)
    }

    function _pushSensorData (state, payload) {
      const key = payload.id
      const timestamp = payload.timestamp
      const value = payload.value

      Vue.set(state.sensors[key], 'value', value)
      Vue.set(state.sensors[key], 'timestamp', timestamp)

      state.sensors[key].history.push({
        x: timestamp,
        y: value
      })

      Vue.set(state.sensors[key], 'history', state.sensors[key].history.slice(-state.nmaxHistory))
    }
  },
  setPageTitle (state, payload) {
    state.pageTitle = payload.title
    state.pageIcon = payload.icon
  }
}

const actions = {}

const store = new Vuex.Store({
  state,
  getters,
  mutations,
  actions
})

export default store



// WEBPACK FOOTER //
// ./src/store/index.js